/**
 * Scroll positions of the app shell's scroll container (<main id="main">), per history entry.
 *
 * Every shell screen scrolls inside the same <main>, which survives route changes, so without this a
 * new screen opens at the previous screen's offset, and coming back from a song (the song page lives
 * outside the shell, which unmounts) always lands at the top of the list. Positions are kept at
 * module scope — they survive the shell unmounting — keyed by location.key. Pure logic; AppShell
 * (components/layout.tsx) wires it to the DOM.
 */

export type ScrollAction =
  /** Back/Forward to an entry we have a position for. */
  | { kind: 'restore'; top: number }
  /** A different screen: start at the top. */
  | { kind: 'top' }
  /** Same screen (search/tab params written with replace, hash links): leave the scroll alone. */
  | { kind: 'keep' };

/** `type` is React Router's navigation type: 'POP' (Back/Forward, first load), 'PUSH' or 'REPLACE'. */
export function scrollActionFor(nav: { type: string; pathname: string; prevPathname: string | null; saved: number | undefined }): ScrollAction {
  if (nav.type === 'POP' && nav.saved !== undefined) return { kind: 'restore', top: nav.saved };
  if (nav.prevPathname !== null && nav.prevPathname === nav.pathname) return { kind: 'keep' };
  return { kind: 'top' };
}

/** History entries remembered (oldest forgotten first). */
export const MAX_REMEMBERED = 200;

export class ScrollMemory {
  private readonly map = new Map<string, number>();

  constructor(private readonly max = MAX_REMEMBERED) {}

  get(key: string): number | undefined {
    return this.map.get(key);
  }

  set(key: string, top: number): void {
    // Re-insert so the most recently used entries are the last to be dropped.
    this.map.delete(key);
    this.map.set(key, Math.max(0, Math.round(top)));
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next().value;
      if (oldest === undefined) break;
      this.map.delete(oldest);
    }
  }

  get size(): number {
    return this.map.size;
  }
}

/** How long a restore keeps waiting for content (data can render a few frames after the screen). */
export const RESTORE_TIMEOUT_MS = 1000;

/**
 * One restore attempt: the offset to apply now and whether to try again next frame (the content is
 * not yet tall enough to reach the saved offset).
 */
export function restoreStep(target: number, maxScroll: number, elapsedMs: number): { top: number; again: boolean } {
  if (maxScroll >= target) return { top: target, again: false };
  return { top: Math.max(0, maxScroll), again: elapsedMs < RESTORE_TIMEOUT_MS };
}

/** The shell's positions (shared by every AppShell mount). */
export const shellScroll = new ScrollMemory();
